import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Dialog,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { apiCallUserCheckedInHistory } from "../../../../Core/Apicall";
import { useLocation } from "react-router-dom";
import CommonPagination from "../../../Common/CommonPagination/CommonPagination";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import dayjs from "dayjs";

interface VenueImage {
  url: string;
  thumbnailUrl?: string;
}
interface Venue {
  _id: string;
  venueName: string;
  coverImage: VenueImage;
  venueImages: VenueImage[];
}

const CheckInTab: React.FC = () => {
  const [tableData, setTableData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [openPreview, setOpenPreview] = useState(false);
  const [usrPhotos, setUsrPhotos] = useState<VenueImage[]>([]);
  const [venueName, setVenueName] = useState("");
  const [selectedMediaForPreview, setSelectedMediaForPreview] = useState<any | null>(null);

  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [totalItems, setTotalItems] = useState(0)
  const totalPages = Math.ceil(totalItems / rowsPerPage)

  const location = useLocation();
  const useData = location.state.user;

  const handleApiCall = async (userId: any) => {
    setLoading(true)
    setError(null)
    try {
      const data = {
        userId,
        offset: page,
        limit: rowsPerPage,
      };
      const res = await apiCallUserCheckedInHistory(data);
      setTableData(res.data);
      setTotalItems(res?.totalCount || 0)
    } catch (err) {
      setError("Failed to fetch image")
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    handleApiCall(useData.userId ? useData.userId : useData._id);
  }, [page, rowsPerPage, useData]);

  const handlePageChange = (value: number) => {
    setPage(value)
  }

  return (
    <Box sx={{ p: "20px" }}>
      <Paper
        className="scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <TableContainer className="scroll-bar" >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Cover Image</TableCell>
                <TableCell>Venue Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Radius</TableCell>
              </TableRow>
            </TableHead>
            {loading === false &&
              <TableBody>
                {tableData.map((venue: any) => (
                  <TableRow key={venue._id}>
                    {/* Venue Name */}
                    <TableCell style={{ cursor: "pointer" }} >
                      <Avatar
                        variant="rounded"
                        src={venue.coverImage.url}
                        sx={{ width: 65, height: 65, borderRadius: "100px" }}
                        onClick={() =>
                          setSelectedMediaForPreview({
                            type: "Image",
                            url: venue.coverImage.url,
                          })
                        }
                      />
                    </TableCell>

                    <TableCell>{venue.venueName}</TableCell>
                    <TableCell>{dayjs(venue.checkInDateTime).format("DD MMM YYYY, hh:mm A")}</TableCell>
                    <TableCell>{venue.checkInRadius} m</TableCell>

                  </TableRow>
                ))}
              </TableBody>}
          </Table>

          {loading === true && (
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>
          )}
          {loading === false &&
            <EmptyData data={tableData} />}
        </TableContainer>

        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={(value) => handlePageChange(value)}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value)
            setPage(1)
          }}
        />
      </Paper>

      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        PaperProps={{
          sx: {
            bgcolor: "#fff",
            position: "relative",
            height: "500px",
            width: "700px",
          },
        }}
      >
        <Typography
          variant="h4"
          sx={{ m: 2, color: "black", textAlign: "center" }}
        >
          {venueName}
        </Typography>

        <IconButton
          onClick={() => setOpenPreview(false)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(255,255,255,0.2)",
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>

        {usrPhotos && usrPhotos.length > 0 && (
          <Box sx={{ textAlign: "center", p: 2 }}>
            {usrPhotos.map((item, idx) => (
              <img
                key={idx}
                src={item.url}
                alt={venueName}
                style={{
                  width: "200px",
                  height: "150px",
                  borderRadius: "8px",
                  margin: "20px",
                }}
                onClick={() =>
                  setSelectedMediaForPreview({
                    type: "Image",
                    url: item.url,
                  })
                }
              />
            ))}
          </Box>
        )}

      </Dialog>

      <Dialog open={!!selectedMediaForPreview} onClose={() => setSelectedMediaForPreview(null)}>
        {selectedMediaForPreview?.type === "Image" ? (
          <Avatar
            src={selectedMediaForPreview.url}
            variant="rounded"
            sx={{ width: 300, height: "auto" }}
          />
        ) : selectedMediaForPreview?.type === "Video" ? (
          <video width="300" controls autoPlay>
            <source src={selectedMediaForPreview.url} type="video/mp4" />
          </video>
        ) : selectedMediaForPreview?.type === "Audio" ? (
          <audio controls style={{ width: "300px" }} autoPlay>
            <source src={selectedMediaForPreview.url} type="audio/mpeg" />
          </audio>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default CheckInTab;
